/**
 * Ein angefangenes Formular überlebt den Seitenwechsel.
 *
 * ═══ WARUM DAS NÖTIG WURDE ═══
 *
 * Wer eine Tour einträgt und dabei nachsehen will, wie der Kunde noch mal
 * heißt, klickt auf „Kunden" — und danach ist das halbe Formular weg. React
 * baut den Bildschirm beim Verlassen ab, und mit ihm den Zustand. Aus dem Büro
 * kam das als „dann muss ich alles nochmal eintippen" zurück, und das ist
 * genau richtig beschrieben.
 *
 * ═══ WARUM `sessionStorage` UND NICHT `localStorage` ═══
 *
 * Der Entwurf überlebt jeden Seitenwechsel und auch ein versehentliches
 * Neuladen. Er überlebt NICHT das Schließen des Tabs — und das ist Absicht.
 *
 * Ein Entwurf in `localStorage` läge nach drei Wochen noch da. Er käme mit
 * einem Ladedatum wieder hoch, das inzwischen in der Vergangenheit liegt, und
 * würde mit einem Klick auf „Tour eintragen" gespeichert, ohne dass jemand das
 * Datum noch einmal liest. Eine Tour mit falschem Ladedatum fällt niemandem
 * auf: Sie steht sofort als „offen" in der Liste und sieht aus wie jede andere.
 *
 * Die Dauer einer Sitzung deckt den gemeldeten Fall vollständig ab und lässt
 * dem Entwurf keine Zeit zu altern.
 *
 * ═══ WAS NICHT MITGEHT ═══
 *
 * Ausgewählte DATEIEN. Ein `File` lässt sich nicht in Text verwandeln und
 * zurück; der Browser gibt den Zugriff auf die Platte mit dem Bildschirm auf.
 * Das wird der Person gesagt, statt es zu verschweigen — sonst klickt sie auf
 * „Tour eintragen" und wundert sich, dass kein Auftragsdokument daranhängt.
 *
 * ═══ WARUM DER SPEICHER NIE ETWAS UMWIRFT ═══
 *
 * `sessionStorage` kann werfen: im privaten Fenster mancher Browser, bei
 * abgeschaltetem Sitzungsspeicher, bei vollem Kontingent. Ein Formular, das
 * deswegen gar nicht erst erscheint, wäre der schlechtere Tausch — deshalb
 * fängt jede Berührung des Speichers ihren Fehler ab und macht ohne Entwurf
 * weiter.
 */

const VORSATZ = 'hj.entwurf.';

/** Den gespeicherten Entwurf holen, oder `null`, wenn keiner (mehr) da ist. */
export function entwurfLesen<T>(schluessel: string): T | null {
  try {
    const roh = sessionStorage.getItem(VORSATZ + schluessel);
    return roh ? (JSON.parse(roh) as T) : null;
  } catch {
    return null;
  }
}

/** Den Entwurf ablegen. Scheitert das, wird nur nicht gesichert. */
export function entwurfSchreiben<T>(schluessel: string, wert: T): void {
  try {
    sessionStorage.setItem(VORSATZ + schluessel, JSON.stringify(wert));
  } catch {
    /* Kein Entwurf ist besser als ein abgestürztes Formular. */
  }
}

/**
 * Den Entwurf wegräumen.
 *
 * Aufzurufen, sobald er nicht mehr gebraucht wird — nach dem Speichern UND
 * nach dem Abbrechen. Bleibt er nach einem Abbruch liegen, taucht er beim
 * nächsten „Neue Tour" wieder auf, und dann legt jemand versehentlich die
 * Tour an, die er gerade verworfen hat.
 */
export function entwurfLoeschen(schluessel: string): void {
  try {
    sessionStorage.removeItem(VORSATZ + schluessel);
  } catch {
    /* Siehe oben. */
  }
}

/**
 * ═══ DATEIEN ÜBERLEBEN WENIGSTENS DEN SEITENWECHSEL (HJ-025) ═══
 *
 * Ein `File` lässt sich nicht in `sessionStorage` legen — wohl aber in den
 * Arbeitsspeicher dieser Seite. Das Backoffice ist EINE Seite: Ein Wechsel
 * von „Disposition" nach „Kunden" und zurück lädt nichts neu, der Speicher
 * hier bleibt. Damit gehen beim gemeldeten Fall (kurz nachsehen, zurück)
 * auch die gewählten Dateien nicht mehr verloren.
 *
 * Beim Neuladen ist er leer, und das ist ehrlich: Dann steht im Hinweis,
 * WELCHE Dateien noch einmal zu wählen sind (die Namen liegen im Entwurf).
 */
const fluechtig = new Map<string, unknown>();

export function fluechtigLesen<T>(schluessel: string): T | null {
  return (fluechtig.get(schluessel) as T | undefined) ?? null;
}

export function fluechtigSchreiben<T>(schluessel: string, wert: T): void {
  fluechtig.set(schluessel, wert);
}

export function fluechtigLoeschen(schluessel: string): void {
  fluechtig.delete(schluessel);
}

/**
 * ═══ BEIM ABMELDEN ALLES WEG ═══
 *
 * Entwürfe gehören zu der Person, die sie getippt hat. Am gemeinsamen
 * Büro-PC meldet sich die nächste im selben Tab an — ohne Aufräumen fände
 * sie das Formular der Vorgängerin offen und gefüllt (Personaldaten, Preise,
 * angehängte PDFs) und könnte es mit einem Klick speichern. Aufgerufen von
 * `Sitzung.tsx`, sobald kein Konto mehr da ist oder ein anderes.
 */
export function alleEntwuerfeLoeschen(): void {
  fluechtig.clear();
  try {
    for (const schluessel of Object.keys(sessionStorage)) {
      if (schluessel.startsWith(VORSATZ)) sessionStorage.removeItem(schluessel);
    }
  } catch {
    /* Siehe oben: Der Speicher darf nichts umwerfen. */
  }
}
